import React, { forwardRef, useId } from 'react';
import clsx from 'clsx';
import styles from './Controls.module.scss';

export const Input = forwardRef(function Input({ className, ...props }, ref) {
  return <input {...props} ref={ref} className={clsx(styles.control, className)} />;
});
export const Select = forwardRef(function Select({ className, ...props }, ref) {
  return <select {...props} ref={ref} className={clsx(styles.control, className)} />;
});
export const Textarea = forwardRef(function Textarea({ className, ...props }, ref) {
  return <textarea rows={4} {...props} ref={ref} className={clsx(styles.control, className)} />;
});

// A single labelable child, or a render function for composite controls.
export function FormField({ id, label, hint, error, required, className, children }) {
  const generatedId = useId();
  const inputId = id || (React.isValidElement(children) && children.props.id) || generatedId;
  const childProps = React.isValidElement(children) ? children.props : {};
  const description = [childProps['aria-describedby'], hint && `${inputId}-hint`, error && `${inputId}-error`].filter(Boolean).join(' ') || undefined;
  const controlProps = {
    id: inputId, required: required ?? childProps.required,
    'aria-invalid': error ? true : childProps['aria-invalid'],
    'aria-describedby': description,
  };
  return <div className={clsx(styles.field, className)}>
    <label className={styles.label} htmlFor={inputId}>{label}{controlProps.required && <span aria-hidden="true"> *</span>}</label>
    {typeof children === 'function' ? children(controlProps) : React.cloneElement(React.Children.only(children), controlProps)}
    {hint && <div id={`${inputId}-hint`} className={styles.hint}>{hint}</div>}
    {error && <div id={`${inputId}-error`} className={styles.error}>{error}</div>}
  </div>;
}

export function Checkbox({ label, hint, error, className, id, role, ...props }) {
  const generatedId = useId();
  const inputId = id || generatedId;
  return <div className={clsx(styles.field, className)}>
    <label className={styles.checkLabel} htmlFor={inputId}>
      <input {...props} id={inputId} type="checkbox" role={role}
        className={role === 'switch' ? styles.switch : styles.checkbox}
        aria-invalid={error ? true : undefined}
        aria-describedby={[props['aria-describedby'], hint && `${inputId}-hint`, error && `${inputId}-error`].filter(Boolean).join(' ') || undefined} />
      <span>{label}</span>
    </label>
    {hint && <div id={`${inputId}-hint`} className={styles.hint}>{hint}</div>}
    {error && <div id={`${inputId}-error`} className={styles.error}>{error}</div>}
  </div>;
}
export function Switch(props) { return <Checkbox {...props} role="switch" />; }

export function RadioGroup({ label, name, value, onChange, options, disabled, required, error, className }) {
  const groupId = useId();
  return <fieldset className={clsx(styles.radioGroup, className)} disabled={disabled} aria-describedby={error ? `${groupId}-error` : undefined}>
    <legend className={styles.label}>{label}</legend>
    <div className={styles.choices}>
      {options.map(option => <label key={option.value} className={styles.choice}>
        <input type="radio" name={name || groupId} value={option.value} required={required}
          checked={value === option.value} disabled={option.disabled}
          onChange={() => onChange(option.value)} />
        <span>{option.label}{option.hint && <small className={styles.hint}>{option.hint}</small>}</span>
      </label>)}
    </div>
    {error && <div id={`${groupId}-error`} className={styles.error}>{error}</div>}
  </fieldset>;
}
